// src/Chat/ChatContext.js
import React,
{
    createContext,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { Global } from '~/App';
import API from '../API';

const ChatContext = createContext();

export function getChatTokenFromUrl() {
    try {
        let params = new URLSearchParams(window.location.search);
        let token = params.get('chatToken') || params.get('token');
        if (token) return token;

        if (window.location.hash && window.location.hash.includes('?')) {
            const hashQuery = window.location.hash.substring(window.location.hash.indexOf('?'));
            params = new URLSearchParams(hashQuery);
            token = params.get('chatToken') || params.get('token');
            if (token) return token;
        }
    } catch (e) {
        console.error("Error al obtener chatToken de URL: ", e);
    }
    return null;
}

function getOrCreateStableAnonymousId() {
    let anonymousId = localStorage.getItem('recaudador_anonymous_id');
    if (!anonymousId) {
        anonymousId = crypto.randomUUID();
        localStorage.setItem('recaudador_anonymous_id', anonymousId);
    }
    return anonymousId;
}

export const ChatProvider = ({ children }) => {
    const [messages, setMessages] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isThinking, setIsThinking] = useState(false);

    const chatToken = getChatTokenFromUrl();
    const hasChatToken = Boolean(chatToken);
    const [shouldConnect, setShouldConnect] = useState(hasChatToken);

    const connectionRef = useRef(null);

    const startChat = () => {
        setShouldConnect(true);
    };

    useEffect(() => {
        if (shouldConnect && !connectionRef.current) {
            const stableId = getOrCreateStableAnonymousId();
            const hubQuery = chatToken
                ? `anonymousId=${stableId}&chatToken=${encodeURIComponent(chatToken)}`
                : `anonymousId=${stableId}`;

            const newConnection = new HubConnectionBuilder()
                .withUrl(`${Global.UrlApi}/chathub?${hubQuery}`, {
                    accessTokenFactory: () => API.Cookie
                })
                .withAutomaticReconnect([0, 2000, 10000, 30000])
                .configureLogging(LogLevel.Information)
                .build();

            connectionRef.current = newConnection;

            // Mensajes salientes del bot
            newConnection.on("ReceiveMessage", (payload) => {
                const newMessage = {
                    type: 'received',
                    source: 'bot',
                    payload,
                    id: payload?.id || (Date.now() + Math.random()),
                    timestamp: new Date()
                };
                setMessages(prevMessages => {
                    if (prevMessages.some(m => m.id === newMessage.id)) return prevMessages;
                    return [...prevMessages, newMessage];
                });
                setIsThinking(false);
            });

            // Mensajes del usuario espejados desde WhatsApp u otros canales
            newConnection.on("ReceiveUserMessage", (data) => {
                const parsedDate = data?.timestamp ? new Date(data.timestamp) : new Date();
                const isValidDate = !isNaN(parsedDate.getTime());
                const newMessage = {
                    type: 'sent',
                    source: data?.source || 'whatsapp',
                    payload: { text: data?.text || '' },
                    id: data?.id || (Date.now() + Math.random()),
                    timestamp: isValidDate ? parsedDate : new Date()
                };
                setMessages(prevMessages => {
                    if (prevMessages.some(m => m.id === newMessage.id)) return prevMessages;
                    return [...prevMessages, newMessage];
                });
                setIsThinking(true);
            });

            // Carga de historial unificado inicial
            newConnection.on("LoadHistory", (rawHistory) => {
                let history = rawHistory;
                if (typeof rawHistory === 'string') {
                    try {
                        history = JSON.parse(rawHistory);
                    } catch (e) {
                        console.error("Error al parsear LoadHistory: ", e);
                    }
                }
                if (Array.isArray(history)) {
                    const mapped = history.map(item => {
                        const parsedDate = item.timestamp ? new Date(item.timestamp) : new Date();
                        const isValidDate = !isNaN(parsedDate.getTime());
                        return {
                            id: item.id || (Date.now() + Math.random()),
                            type: item.type,
                            source: item.source || (item.type === 'received' ? 'bot' : 'web'),
                            payload: item.payload || (item.text ? { text: item.text } : {}),
                            timestamp: isValidDate ? parsedDate : new Date()
                        };
                    });
                    setMessages(mapped);
                    setIsThinking(false);
                }
            });

            newConnection.onreconnected(() => {
                setIsConnected(true);
            });

            newConnection.start()
                .then(() => {
                    setIsConnected(true);
                })
                .catch(e => console.error('Error de conexión con socket: ', e));
        }

        return () => {
            if (connectionRef.current && connectionRef.current.state === 'Connected') {
                connectionRef.current.stop();
                connectionRef.current = null;
            }
        };
    }, [shouldConnect]);

    const disableAllPreviousButtons = (currentMessages) => {
        return currentMessages.map(msg => {
            if (msg.type === 'received' && msg.payload.options && !msg.payload.buttonsDisabled) {
                return { ...msg, payload: { ...msg.payload, buttonsDisabled: true } };
            }
            return msg;
        });
    };

    const sendMessage = (messageText) => {
        if (connectionRef.current && isConnected) {
            setIsThinking(true);
            const updatedMessages = disableAllPreviousButtons(messages);

            connectionRef.current.invoke("HandleIncomingMessage", messageText)
                .catch(e => {
                    console.error("Error al enviar mensaje: ", e);
                    setIsThinking(false);
                });

            const newMessage = {
                type: 'sent',
                source: 'web',
                payload: { text: messageText },
                id: Date.now() + Math.random(),
                timestamp: new Date()
            };
            setMessages([...updatedMessages, newMessage]);
        }
    };

    const sendButtonPress = (messageId, payloadId, title) => {
        if (connectionRef.current && isConnected) {
            setIsThinking(true);
            const updatedMessages = messages.map(msg => {
                if (msg.id === messageId || (msg.type === 'received' && msg.payload.options && !msg.payload.buttonsDisabled)) {
                    return { ...msg, payload: { ...msg.payload, buttonsDisabled: true } };
                }
                return msg;
            });

            connectionRef.current.invoke("HandleButtonPress", payloadId, title)
                .catch(e => {
                    console.error("Error al presionar botón: ", e);
                    setIsThinking(false);
                });

            const newMessage = {
                type: 'sent',
                source: 'web',
                payload: { text: title },
                id: Date.now() + Math.random(),
                timestamp: new Date()
            };
            setMessages([...updatedMessages, newMessage]);
        }
    };

    const sendFile = (file) => {
        if (connectionRef.current && isConnected && file) {
            setIsThinking(true);

            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const fileB64 = reader.result;

                connectionRef.current.invoke("HandleIncomingFile", file.name, fileB64, file.type)
                    .catch(e => {
                        console.error("Error al enviar archivo: ", e);
                        setIsThinking(false);
                    });

                const updatedMessages = disableAllPreviousButtons(messages);
                const newMessage = {
                    type: 'sent',
                    source: 'web',
                    payload: { text: `Archivo enviado: ${file.name}` },
                    id: Date.now() + Math.random(),
                    timestamp: new Date()
                };
                setMessages([...updatedMessages, newMessage]);
            };
            reader.onerror = (error) => {
                console.error("Error al leer el archivo: ", error);
                setIsThinking(false);
            };
        }
    };

    const value = {
        messages,
        isConnected,
        isThinking,
        hasChatToken,
        sendMessage,
        sendButtonPress,
        sendFile,
        startChat
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = () => {
    return useContext(ChatContext);
};